import { getCookie } from './cookies';
import { BT_COGNITO_CLIENT_ID_PROD, BT_COGNITO_CLIENT_ID_QA, isProduction } from '../constants';

const PARENT_WAIT_MS = 3000;
const PARENT_POLL_INTERVAL_MS = 100;
const PARENT_SESSION_TIMEOUT_MS = 3000;
const EXPIRY_BUFFER_SECONDS = 60;

type GetCurrentSession = () => Promise<any>;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

function getParentSessionFn(): GetCurrentSession | null {
  try {
    if (window === window.top) return null;
    const library = (window.parent as any)?.amplifyLibrary;
    if (typeof library?.getCurrentSession !== 'function') return null;
    return () => library.getCurrentSession();
  } catch {
    return null;
  }
}

function isFramed(): boolean {
  try {
    return window !== window.top;
  } catch {
    return true;
  }
}

async function waitForParentSessionFn(): Promise<GetCurrentSession | null> {
  if (!isFramed()) return null;
  const deadline = Date.now() + PARENT_WAIT_MS;
  let sessionFn = getParentSessionFn();
  while (!sessionFn && Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await sleep(PARENT_POLL_INTERVAL_MS);
    sessionFn = getParentSessionFn();
  }
  return sessionFn;
}

function decodeJwtExp(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const exp = JSON.parse(atob(padded))?.exp;
    return typeof exp === 'number' ? exp : null;
  } catch {
    return null;
  }
}

function isUsable(token: string | null | undefined): token is string {
  if (!token) return false;
  const exp = decodeJwtExp(token);
  return exp !== null && exp - Date.now() / 1000 > EXPIRY_BUFFER_SECONDS;
}

function extractIdToken(session: any): string | null {
  return session?.idToken?.jwtToken ?? session?.getIdToken?.()?.getJwtToken?.() ?? null;
}

async function readParentToken(sessionFn: GetCurrentSession): Promise<string | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), PARENT_SESSION_TIMEOUT_MS);
    });
    const session = await Promise.race([sessionFn(), timeout]);
    const token = extractIdToken(session);
    return isUsable(token) ? token : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

const JS_COOKIE_UNENCODED = /%(23|24|26|2B|5E|60|7C)/gi;

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function usernameCandidates(lastAuthUser: string): string[] {
  const specForm = encodeURIComponent(lastAuthUser);
  const jsCookieForm = encodeURIComponent(safeDecode(lastAuthUser)).replace(
    JS_COOKIE_UNENCODED,
    (match) => decodeURIComponent(match)
  );
  return Array.from(new Set([specForm, jsCookieForm]));
}

function readCookieToken(): string | null {
  const clientId = isProduction() ? BT_COGNITO_CLIENT_ID_PROD : BT_COGNITO_CLIENT_ID_QA;
  if (!clientId) return null;
  const prefix = `CognitoIdentityServiceProvider.${clientId}`;
  const lastAuthUser = getCookie(`${prefix}.LastAuthUser`);
  if (!lastAuthUser) return null;
  const candidates = usernameCandidates(lastAuthUser);
  for (let i = 0; i < candidates.length; i += 1) {
    const token = getCookie(`${prefix}.${candidates[i]}.idToken`);
    if (isUsable(token)) return token;
  }
  return null;
}

export async function getBtIdToken(): Promise<string | null> {
  const sessionFn = await waitForParentSessionFn();
  if (sessionFn) {
    const parentToken = await readParentToken(sessionFn);
    if (parentToken) return parentToken;
  }
  return readCookieToken();
}

export async function refreshBtIdToken(): Promise<string | null> {
  const sessionFn = getParentSessionFn();
  if (sessionFn) {
    const parentToken = await readParentToken(sessionFn);
    if (parentToken) return parentToken;
  }
  return readCookieToken();
}
